import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { MenuItem, ListItemIcon, ListItemText, Grid, Box, Typography, Divider } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';

import MuiDialog from '../../SystemDesign/MuiDialog';
import FormInput from '../../SystemDesign/FormInput';
import FormSelect from '../../SystemDesign/FormSelect';
import { useUpdateEvaluationMutation } from '../../services/SkillsManagement';
import { useDispatch } from 'react-redux';
import { UPDATE_EVALUATION } from '../../Reducers/Actions';
import { SnackBarContext } from '../../Context/SnackBarContextProvider';
import FileUpload from '../../SystemDesign/fileUpload';
import PracticalEvidenceList from './PracticalEvidences/PracticalEvidenceList';
import useUploadEvaluationEvidences from '../../hooks/useUploadEvaluationEvidences';

/**
 * UpdateEvaluation
 * Dialog to update an existing evaluation (score, level, comments, date).
 * Usage mirrors other entity update dialogs, e.g. `UserCompany/UpdateEmployee.jsx`.
 *
 * Props:
 * - row: { original: evaluationLike }
 */
const UpdateEvaluation = ({ row, onUpdated, onUpdating }) => {
  const evaluation = row?.original || {};

  // Resolve evaluation id from multiple shapes
  const evaluationId = useMemo(() => {
    return (
      evaluation?.evaluation_id ?? evaluation?.id ?? evaluation?.evaluationId ?? null
    );
  }, [evaluation]);

  const dispatch = useDispatch();
  const { setInfoAlert, setOpenSnackBar } = useContext(SnackBarContext);
  const [updateEvaluation, { isLoading }] = useUpdateEvaluationMutation();
  const { uploadForSkill } = useUploadEvaluationEvidences();

  // Ensure date renders in <input type="date"/> as yyyy-MM-dd
  const normalizeDateForInput = (value) => {
    if (!value) return '';
    // If already yyyy-MM-dd
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    try {
      const d = new Date(value);
      if (isNaN(d.getTime())) return '';
      // convert to local date representation yyyy-MM-dd
      const tz = d.getTimezoneOffset();
      const local = new Date(d.getTime() - tz * 60000);
      return local.toISOString().slice(0, 10);
    } catch (_) {
      return '';
    }
  };

  // Initial values
  const initialValues = useMemo(
    () => ({
      score: evaluation?.score ?? '',
      comments: evaluation?.comments ?? '',
      // Support both numeric and string level; map numeric 1/2/3 to canonical string
      level:
        typeof evaluation?.level === 'number'
          ? (evaluation.level === 1
              ? 'awareness'
              : evaluation.level === 2
              ? 'skilled_application'
              : evaluation.level === 3
              ? 'management'
              : '')
          : evaluation?.level ?? '',
      evaluation_date: normalizeDateForInput(
        evaluation?.evaluation_date ?? evaluation?.evaluationDate ?? ''
      ),
    }),
    [evaluation]
  );

  const [formValues, setFormValues] = useState(initialValues);
  useEffect(() => setFormValues(initialValues), [initialValues]);

  // File upload state for new practical evidence
  const [selectedFiles, setSelectedFiles] = useState([]);

  const validationSchema = Yup.object({
    score: Yup.number()
      .nullable()
      .transform((v, o) => (o === '' || o == null ? null : v))
      .min(0, 'Score must be at least 0')
      .max(10, 'Score must be at most 10'),
    comments: Yup.string().nullable(),
    level: Yup.string().oneOf(['', 'awareness', 'skilled_application', 'management']),
    evaluation_date: Yup.mixed(), // allow optional change
  });

  const levelOptions = [
    { id: '', title: 'Unspecified' },
    { id: 'awareness', title: 'Awareness (1)' },
    { id: 'skilled_application', title: 'Skilled Application (2)' },
    { id: 'management', title: 'Management (3)' },
  ];

  const formFields = [
    { type: 'number', name: 'score', label: 'Score (0-10)', required: false },
    { type: 'select', name: 'level', label: 'Level', options: levelOptions, required: false },
    { type: 'date', name: 'evaluation_date', label: 'Evaluation Date', required: false },
    { type: 'text', name: 'comments', label: 'Comments', required: false },
  ];

  function getFieldGridProps(field) {
    switch (field.name) {
      case 'score':
      case 'level':
      case 'evaluation_date':
        return { xs: 12, md: 6, lg: 4 };
      case 'comments':
        return { xs: 12 };
      default:
        return { xs: 12, md: 6 };
    }
  }

  const submitPromiseRef = useRef(null);

  const handleChange = (e, formik) => {
    const { name, value } = e.target;
    if (name === 'score') {
      const v = value === '' ? '' : Number(value);
      formik.setFieldValue(name, isNaN(v) ? '' : v);
    } else {
      formik.setFieldValue(name, value);
    }
  };

  const handleSubmit = async (values, actions) => {
    // Notify parent that an update cycle is starting
    try { if (typeof onUpdating === 'function') onUpdating(); } catch (_) {}
    if (!evaluationId) {
      setInfoAlert({ severity: 'error', message: 'Missing evaluation id' });
      setOpenSnackBar(true);
      actions.setSubmitting(false);
      if (submitPromiseRef.current) {
        submitPromiseRef.current(false);
        submitPromiseRef.current = null;
      }
      return;
    }
    try {
      // Build minimal update payload (omit empty fields)
      const payload = {};
      if (values.score !== '' && values.score != null) payload.score = Number(values.score);
      if (values.comments != null) payload.comments = values.comments;
      if (values.level) payload.level = values.level; // backend accepts canonical strings
      if (values.evaluation_date) payload.evaluation_date = String(values.evaluation_date);

      // If nothing to update
      const nothingToUpdate = Object.keys(payload).length === 0;

      let updated = null;
      if (!nothingToUpdate) {
        const res = await updateEvaluation({
          evaluationId: Number(evaluationId),
          body: payload,
        }).unwrap();
        updated = res?.data || res?.evaluation || res;
        dispatch({ type: UPDATE_EVALUATION, payload: updated });
      }

      // After updating fields (or even if no field changes), upload selected evidence if any
      const skillId = evaluation?.skill_id || evaluation?.skillId || evaluation?.skill?.id;
      if (selectedFiles && selectedFiles.length > 0) {
        try {
          await uploadForSkill({
            evaluationId,
            skillId: skillId ? Number(skillId) : undefined,
            files: selectedFiles,
            level: values.level || null,
            score: (values.score !== '' && values.score != null) ? Number(values.score) : null,
            comment: values.comments || '',
          });
          setInfoAlert({ severity: 'success', message: nothingToUpdate ? 'Evidence uploaded successfully' : 'Evaluation updated and evidence uploaded' });
          setOpenSnackBar(true);
          setSelectedFiles([]);
        } catch (e) {
          // uploadForSkill already logs/toasts internally when needed
        }
      } else if (!nothingToUpdate) {
        setInfoAlert({ severity: 'success', message: 'Evaluation updated successfully' });
        setOpenSnackBar(true);
      } else {
        // Neither field changes nor file chosen
        setInfoAlert({ severity: 'info', message: 'No changes to update' });
        setOpenSnackBar(true);
      }
      // Notify parent after successful update
      try { if (typeof onUpdated === 'function') onUpdated(updated); } catch (_) {}
      if (submitPromiseRef.current) {
        submitPromiseRef.current(true);
        submitPromiseRef.current = null;
      }
    } catch (error) {
      console.error('Error updating evaluation:', error);
      setInfoAlert({
        severity: 'error',
        message: error?.data?.message || error?.message || 'Failed to update evaluation',
      });
      setOpenSnackBar(true);
      if (submitPromiseRef.current) {
        submitPromiseRef.current(false);
        submitPromiseRef.current = null;
      }
    }
    actions.setSubmitting(false);
  };

  if (!evaluationId) return null;

  const triggerButton = (
    <MenuItem>
      <ListItemIcon>
        <EditIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>Edit</ListItemText>
    </MenuItem>
  );

  return (
    <MuiDialog
      icon={<EditIcon />}
      triggerButton={triggerButton}
      title="Update Evaluation"
      subtitle={`Editing evaluation #${evaluationId}`}
      maxWidth="md"
      content={
        <Formik
          initialValues={formValues}
          enableReinitialize
          validationSchema={validationSchema}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={handleSubmit}
        >
          {(formik) => (
            <Form id="updateEvaluationForm" autoComplete="off">
              <Grid container spacing={2}>
                {formFields.map((field, idx) => (
                  <Grid item size={{ ...getFieldGridProps(field) }} key={idx}>
                    {field.type === 'text' || field.type === 'number' || field.type === 'date' ? (
                      <FormInput
                        type={field.type}
                        name={field.name}
                        label={field.label}
                        required={field.required}
                        value={formik.values[field.name]}
                        onChange={(e) => handleChange(e, formik)}
                        onBlur={formik.handleBlur}
                        error={formik.touched[field.name] && Boolean(formik.errors[field.name])}
                        helperText={formik.touched[field.name] && formik.errors[field.name]}
                        InputLabelProps={{ shrink: true }}
                      />
                    ) : (
                      <FormSelect
                        name={field.name}
                        label={field.label}
                        options={field.options}
                        required={field.required}
                        value={formik.values[field.name]}
                        onChange={(e) => handleChange(e, formik)}
                        onBlur={formik.handleBlur}
                        error={formik.touched[field.name] && Boolean(formik.errors[field.name])}
                        helperText={formik.touched[field.name] && formik.errors[field.name]}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  </Grid>
                ))}
              </Grid>

              {/* Practical Evidence Section */}
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Practical Evidence</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Upload a new evidence file for this evaluation or manage existing evidences below.
                </Typography>
                <Grid container spacing={2}>
                  <Grid item size={{ xs: 12 }}>
                    <FileUpload
                      selectedFiles={selectedFiles}
                      setSelectedFiles={setSelectedFiles}
                      onFileSelect={(fileItem) => {
                        // no-op: evidence metadata will be taken from current form values
                      }}
                      label="Add evidence file"
                      description="images, videos, or any file"
                      acceptedFormats="or browse files on your computer"
                      browseButtonLabel="Choose file"
                      removeButtonLabel="Remove"
                      uploadedFileLabel="Selected file"
                      multiple={false}
                    />
                  </Grid>
                  <Grid item size={{ xs: 12 }}>
                    <PracticalEvidenceList evaluationId={evaluationId} />
                  </Grid>
                </Grid>
              </Box>
            </Form>
          )}
        </Formik>
      }
      existAction={true}
      actionTitle="Update"
      actionFunction={async () => {
        const formEl = document.querySelector('#updateEvaluationForm');
        if (!formEl) return false;
        return new Promise((resolve) => {
          submitPromiseRef.current = resolve;
          formEl.requestSubmit();
        });
      }}
      isLoading={isLoading}
    />
  );
};

// For menu integrations, expose icon/label
UpdateEvaluation.menuIcon = <EditIcon fontSize="small" />;
UpdateEvaluation.menuLabel = 'Edit';

export default UpdateEvaluation;

